import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getInvoice,
  getInvoicePDF,
  sendInvoice,
  syncInvoice,
  voidInvoice,
  refundInvoice,
} from "../../services/invoices";
import ConfirmModal from "../../components/ui/ConfirmModal";

const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInvoice(id);

      if (response && response.success !== false) {
        const data = response.data ?? response;
        setInvoice(data?.invoice ?? data);
      } else {
        setError(response?.error || "Failed to load invoice");
      }
    } catch (err) {
      console.error("Error loading invoice:", err);
      setError(err.message || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await getInvoicePDF(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoice?.invoiceNumber || id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (err) {
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleSendInvoice = async () => {
    try {
      setSending(true);
      const response = await sendInvoice(id);
      if (response && response.success !== false) {
        toast.success("Invoice sent successfully");
        loadInvoice();
      } else {
        toast.error(response?.error || "Failed to send invoice");
      }
    } catch (err) {
      toast.error(err.message || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  const handleSyncInvoice = async () => {
    try {
      setSyncing(true);
      const response = await syncInvoice(id);
      if (response && response.success !== false) {
        toast.success("Invoice synced successfully");
        loadInvoice();
      } else {
        toast.error(response?.error || "Failed to sync invoice");
      }
    } catch (err) {
      toast.error(err.message || "Failed to sync invoice");
    } finally {
      setSyncing(false);
    }
  };

  const handleVoidInvoice = async () => {
    try {
      setVoiding(true);
      const response = await voidInvoice(id);
      if (response && response.success !== false) {
        toast.success(response.message || "Invoice voided successfully");
        loadInvoice();
      } else {
        toast.error(response?.error || "Failed to void invoice");
      }
    } catch (err) {
      toast.error(err.message || "Failed to void invoice");
    } finally {
      setVoiding(false);
    }
  };

  const handleRefundInvoice = async () => {
    try {
      setRefunding(true);
      const response = await refundInvoice(id);
      if (response && response.success !== false) {
        toast.success(response.message || "Refund processed successfully");
        loadInvoice();
      } else {
        toast.error(response?.error || "Failed to refund invoice");
      }
    } catch (err) {
      toast.error(err.message || "Failed to refund invoice");
    } finally {
      setRefunding(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: "bg-gray-100 text-gray-700",
      SUBMITTED: "bg-blue-100 text-blue-700",
      AUTHORISED: "bg-green-100 text-green-700",
      PAID: "bg-emerald-100 text-emerald-700",
      REFUNDED: "bg-amber-100 text-amber-800",
      VOIDED: "bg-red-100 text-red-700",
      DELETED: "bg-gray-100 text-gray-500",
    };
    return badges[status] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading invoice...</div>
        </div>
      </div>
    );
  }

  const invoiceStatus = (invoice?.status ?? "").toString().toUpperCase();

  if (error || !invoice) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error || "Invoice not found"}
        </div>
        <button
          onClick={() => navigate("/invoices")}
          className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Invoice Details
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Invoice #{invoice.invoiceNumber}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Download PDF
          </button>
          {invoiceStatus === "AUTHORISED" && (
            <>
              <button
                onClick={() => setSendModalOpen(true)}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {sending ? "Sending..." : "Send Invoice"}
              </button>
              <button
                onClick={handleSyncInvoice}
                disabled={syncing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {syncing ? "Syncing..." : "Sync to Xero"}
              </button>
              <button
                onClick={() => setVoidModalOpen(true)}
                disabled={voiding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {voiding ? "Voiding..." : "Void Invoice"}
              </button>
            </>
          )}
          {invoiceStatus === "PAID" && (
            <>
              <button
                onClick={() => setRefundModalOpen(true)}
                disabled={refunding}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {refunding ? "Refunding..." : "Refund"}
              </button>
              <button
                onClick={() => setVoidModalOpen(true)}
                disabled={voiding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {voiding ? "Voiding..." : "Void Invoice"}
              </button>
            </>
          )}
          {invoiceStatus === "REFUNDED" && (
            <span className="text-sm text-amber-700 font-medium">
              Refunded
              {invoice.refundedAt &&
                ` on ${new Date(invoice.refundedAt).toLocaleDateString()}`}
              {invoice.refundedBy &&
                (invoice.refundedBy.firstName || invoice.refundedBy.lastName) &&
                ` by ${[invoice.refundedBy.firstName, invoice.refundedBy.lastName].filter(Boolean).join(" ")}`}
            </span>
          )}
          <button
            onClick={() => navigate("/invoices")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Back to Invoices
          </button>
        </div>
      </div>

      {/* Invoice Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Invoice Header */}
        <div className="flex justify-between items-start mb-6 pb-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Invoice #{invoice.invoiceNumber}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Date:{" "}
              {invoice.date
                ? new Date(invoice.date).toLocaleDateString()
                : "N/A"}
            </p>
            {invoice.dueDate && (
              <p className="text-sm text-gray-500">
                Due Date: {new Date(invoice.dueDate).toLocaleDateString()}
              </p>
            )}
            {invoice.reference && (
              <p className="text-sm text-gray-500 mt-1">
                Reference: {invoice.reference}
              </p>
            )}
            {invoice.paymentMethodIntention && (
              <p className="text-sm text-gray-500 mt-1">
                Payment method intention:{" "}
                <span className="font-medium text-gray-700">
                  {invoice.paymentMethodIntention === "credit_card"
                    ? "Credit card"
                    : invoice.paymentMethodIntention === "bank_transfer"
                      ? "Bank transfer"
                      : invoice.paymentMethodIntention}
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-right">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(invoiceStatus)}`}
              >
                {invoiceStatus || invoice.status}
              </span>
              {invoiceStatus === "REFUNDED" && invoice.refundedAt && (
                <p className="text-sm text-amber-700 mt-1">
                  Refunded on{" "}
                  {new Date(invoice.refundedAt).toLocaleDateString()}
                  {invoice.refundedBy &&
                    (invoice.refundedBy.firstName ||
                      invoice.refundedBy.lastName) && (
                      <>
                        {" "}
                        by{" "}
                        {[
                          invoice.refundedBy.firstName,
                          invoice.refundedBy.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </>
                    )}
                </p>
              )}
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                invoice.syncStatus === "synced"
                  ? "bg-green-100 text-green-700"
                  : invoice.syncStatus === "error"
                    ? "bg-red-100 text-red-700"
                    : "bg-yellow-100 text-yellow-700"
              }`}
            >
              {invoice.syncStatus || "pending"}
            </span>
          </div>
        </div>

        {/* Customer Info */}
        {invoice.customerId && (
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Customer
            </h3>
            <div className="text-sm text-gray-600">
              {invoice.customerId.firstName || invoice.customerId.lastName ? (
                <p>
                  {invoice.customerId.firstName} {invoice.customerId.lastName}
                </p>
              ) : null}
              {invoice.customerId.email && (
                <p className="text-gray-500">{invoice.customerId.email}</p>
              )}
            </div>
          </div>
        )}

        {/* Line Items */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Line Items
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Quantity
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Unit Price
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoice.lineItems && invoice.lineItems.length > 0 ? (
                  invoice.lineItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.description}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {item.quantity || 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {invoice.currency || "GBP"}{" "}
                        {(item.unitAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                        {invoice.currency || "GBP"}{" "}
                        {(item.lineAmount || item.unitAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-3 text-sm text-gray-500 text-center"
                    >
                      No line items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            {invoice.totalTax > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">VAT:</span>
                <span className="text-gray-900">
                  {invoice.currency || "GBP"} {invoice.totalTax.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200">
              <span className="text-gray-900">Total:</span>
              <span className="text-gray-900">
                {invoice.currency || "GBP"} {(invoice.total || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Link to License */}
      {invoice.licenceId && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-2">Related Licence:</p>
          <button
            onClick={() =>
              navigate(
                `/licence-details/${invoice.licenceId._id || invoice.licenceId}`,
              )
            }
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            View Licence #{invoice.licenceId.refNumber || invoice.licenceId}
          </button>
        </div>
      )}

      {/* Xero Info */}
      {invoice.xeroInvoiceId && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-2">Xero Invoice ID:</p>
          <p className="text-sm font-mono text-gray-900">
            {invoice.xeroInvoiceId}
          </p>
        </div>
      )}

      {/* Send invoice confirmation */}
      <ConfirmModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        onConfirm={handleSendInvoice}
        title="Send invoice"
        message="Send this invoice to the customer via email?"
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="primary"
      />

      {/* Void invoice confirmation */}
      <ConfirmModal
        isOpen={voidModalOpen}
        onClose={() => setVoidModalOpen(false)}
        onConfirm={handleVoidInvoice}
        title="Void invoice"
        message="This invoice will be cancelled in Xero (if synced) and you will be able to generate a new invoice for this licence."
        confirmText="Void invoice"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Refund confirmation */}
      <ConfirmModal
        isOpen={refundModalOpen}
        onClose={() => setRefundModalOpen(false)}
        onConfirm={handleRefundInvoice}
        title="Refund invoice"
        message="The full amount will be refunded to the customer via Stripe. The invoice will be marked as Refunded (Xero will keep the invoice as Paid and add a credit note for the refund)."
        confirmText="Refund"
        cancelText="Cancel"
        confirmVariant="primary"
      />
    </div>
  );
};

export default InvoiceDetails;
